import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DEFAULT_CATEGORY_ID, useCategoryConfig, type CatConfig } from './category-config';
import { CategorySelector } from './category-selector';
import { DynamicFilterBar } from './dynamic-filter-bar';
import { parseSearchText } from './parse-search-text';
import { SmartSearchBar } from './smart-search-bar';
import { type Suggestion } from './suggestions';

/**
 * Hero search orchestrator. Owns the shared state (category, location query,
 * per-category filter values) and composes the three independent layers:
 * CategorySelector → DynamicFilterBar → SmartSearchBar. Each layer stays a
 * dedicated component; this shell wires them together and turns the combined
 * state into a `/search` URL.
 *
 * The two search routes are deliberately separate:
 *
 * - **Free text** (typed or dictated) is a UNIVERSAL search over every project
 *   on the site. It ignores the category strip and the filter bar entirely —
 *   a visitor who types a builder's name should find that builder whether or
 *   not the (default) Residential tab happens to be selected, and structured
 *   intent expressed in words is turned into real filter params instead
 *   ("3 bhk" → `config=3`, i.e. every 3 BHK project). See
 *   {@link parseSearchText}.
 * - **The filter bar** drives the search when the text field is empty:
 *   category scoping plus whichever fields the visitor set.
 */

/** Deliberately bare — the bar searches everything, so it needs no qualifier. */
const SEARCH_PLACEHOLDER = 'Search';
export function HeroSearch() {
  const navigate = useNavigate();

  const [categoryId, setCategoryId] = useState<string>(DEFAULT_CATEGORY_ID);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Suggestion | null>(null);
  // Config-driven per-category field values (reset when the category changes).
  const [values, setValues] = useState<Record<string, string>>({});

  const categories = useCategoryConfig();
  const cat: CatConfig = categories.find((c) => c.id === categoryId) ?? categories[0]!;

  // Switching category loads that category's own workflow — clear the previous
  // category's field values (they differ) but keep the free-text location.
  const selectCategory = (id: string) => {
    setCategoryId(id);
    setValues({});
  };
  const setValue = (key: string, v: string) => setValues((prev) => ({ ...prev, [key]: v }));

  const buildParams = (loc?: Suggestion | null, queryOverride?: string): URLSearchParams => {
    const p = new URLSearchParams();
    // `queryOverride` is for callers that just changed the query and need to
    // submit it in the same tick — e.g. voice search — before React has
    // re-rendered with the new `query` state, so reading the state directly
    // here would still see the previous value.
    const text = (queryOverride ?? query).trim();

    // ── Universal free-text search ───────────────────────────────────────────
    // Nothing from the category strip or the filter bar is applied: the query
    // is matched against the whole inventory. Only what the text itself asks
    // for becomes a filter.
    if (text) {
      const { q, config } = parseSearchText(text);
      if (q) p.set('q', q);
      if (config.length) p.set('config', config.join(','));
      return p;
    }

    // ── Filter-bar search (empty text field) ─────────────────────────────────
    // Category scoping first.
    for (const [k, v] of Object.entries(cat.baseParams)) p.set(k, v);
    // A resolved location (e.g. a "Near me" city) stands in for the free text.
    const chosen = loc ?? selected;
    if (chosen) {
      for (const [k, v] of Object.entries(chosen.params)) p.set(k, v);
    }
    // Field values override base/location for the same param when set.
    for (const f of cat.fields) {
      const v = values[f.key];
      if (!v) continue;
      if (f.control === 'budget') {
        const [min, max] = v.split('-');
        if (min) p.set('priceMin', min);
        if (max) p.set('priceMax', max);
      } else if (f.param) {
        p.set(f.param, v);
      }
    }
    return p;
  };

  const submit = (loc?: Suggestion | null, queryOverride?: string) =>
    navigate(`/search?${buildParams(loc, queryOverride).toString()}`);

  return (
    <div className="w-full">
      {/* Layer 1 — category selector. Kept close to the filter bar below it
          (not the wider rhythm used elsewhere in the hero) since the two are
          functionally linked — the filter bar's fields change based on which
          category is selected here. */}
      <div className="mb-2">
        <CategorySelector categoryId={categoryId} onSelect={selectCategory} />
      </div>

      <form
        role="search"
        aria-label="Property search"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="flex flex-col gap-5 md:gap-6"
      >
        {/* Layer 2 — dynamic filter bar (filters + Search) */}
        <div className="rounded-2xl p-3">
          <DynamicFilterBar cat={cat} values={values} setValue={setValue} />
        </div>

        {/* Layer 3 — global smart search bar (independent of the filters).
            Full width, matching the filter bar above it, so the category
            selector / filter bar / search bar all share the same left and
            right edges instead of the search bar reading as an oddly
            narrower, disconnected element. */}
        <div className="w-full">
          <SmartSearchBar
            placeholder={SEARCH_PLACEHOLDER}
            query={query}
            setQuery={setQuery}
            setSelected={setSelected}
            onSubmit={(queryOverride) => submit(undefined, queryOverride)}
          />
        </div>
      </form>
    </div>
  );
}
